/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited 2023, 2024.
 */

import { Page as PageComponent } from '@/components/Page';
import { useNotifications } from '@/data/Content/Notifications';
import { getStoreLocale } from '@/data/Content/StoreLocale-Server';
import { getLocalization } from '@/data/Localization';
import { getSettings, useSettings } from '@/data/Settings';
import { ElasticSearchErrorResponse, TransactionErrorResponse } from '@/data/types/Basic';
import { Cache } from '@/data/types/Cache';
import { getCache } from '@/data/utils/getCache';
import { getPagePropsForServer } from '@/data/utils/getPagePropsForServer';
import { processError } from '@/data/utils/processError';
import { ERROR_RETRY_INTERVAL, onErrorRetry } from '@/data/utils/onErrorRetry';
import { setCacheHeaderIfRequiredForCDN } from '@/data/utils/setCacheHeaderIfRequiredForCDN';
import { setReferrerCookie } from '@/data/utils/setReferrerCookie';
import type { GetServerSideProps, NextPage } from 'next';
import nextConfig from 'next.config';
import { useCallback } from 'react';
import { SWRConfig } from 'swr';

import { TrackingHooks } from 'presentation/events/components/TrackingHooks';
import { useRemoveGuestAddresses } from 'presentation/events/hooks/useRemoveGuestAddresses';

interface Props {
	fallback?: Record<string, unknown>;
}

const GlobalSWRConfig = {
	revalidateOnFocus: false, // disable focus re-validations
	revalidateIfStale: false, // disable auto re-validations
	errorRetryInterval: ERROR_RETRY_INTERVAL,
	// Bounded retry: skip 404, cap at MAX_ERROR_RETRY_COUNT, exponential backoff + jitter.
	// Replaces SWR's default unlimited retry that turned backend saturation into a retry storm.
	onErrorRetry,
};

const Page: NextPage<Props> = ({ fallback }) => {
	const { settings } = useSettings();
	const { notifyError } = useNotifications();
	const onError = useCallback(
		(error: unknown, _key: string) => {
			notifyError(processError(error as TransactionErrorResponse | ElasticSearchErrorResponse));
		},
		[notifyError]
	);
	useRemoveGuestAddresses();
	// the check for `settings.storeId` is needed when navigating from a statically generated page
	// to this server-side rendered page. Different from initial page load, in this scenario,
	// the settings are from pageProps async ajax call
	return settings.storeId ? (
		<SWRConfig value={{ fallback, onError, ...GlobalSWRConfig }}>
			<TrackingHooks />
			<PageComponent />
		</SWRConfig>
	) : null;
};

export default Page;

export const getServerSideProps: GetServerSideProps = async (context) => {
	const cache: Cache = getCache();
	const settings = await getSettings(cache, context);
	const { res } = context;
	// for metrics
	Object.assign(res, { hclData: { storeId: settings?.storeId } });

	// getPagePropsForServer and the locale/Routes lookup both only depend on
	// `settings` (already resolved above), not on each other's output, so they
	// run concurrently instead of the Routes lookup waiting behind the full
	// page-data fetch for no reason.
	const [pageProps, routes] = await Promise.all([
		getPagePropsForServer({ context, cache }),
		getStoreLocale({ cache, context }).then(({ localeName: locale }) =>
			getLocalization(cache, locale || nextConfig.i18n?.defaultLocale || 'default', 'Routes')
		),
	]);
	const isMobileApp = context.req.headers['user-agent']?.includes('THDMXAPP');
	/**
	 * set cache-control header so that CDN can cache it
	 */
	setCacheHeaderIfRequiredForCDN({ pageProps, context, routes, settings });
	setReferrerCookie(context, settings);
	const protocol = context.req.headers['x-forwarded-proto']?.toString() ?? 'http';
	const host = context.req.headers.host;
	const origin = `${protocol}://${host}`;
	const { props = { fallback: {} }, ...rest } = pageProps;
	return {
		...rest,
		props: {
			...props,
			settings: {
				...settings,
				isMobileApp,
				origin,
			},
		},
	};
};
